<template>
  <FullPageLayout class="bg-[#f7f8fb]">
    <template v-if="fetchState.pending">
      <div class="flex justify-center my-5">
        <OLoading />
      </div>
    </template>
    <template v-else-if="fetchState.error">
      <ErrorPageLayout :error="fetchState.error" />
    </template>
    <template v-else>
      <VitrinHeader class="w-auto" :categories="allCategories" />
      <div class="max-w-[1100px] w-full mx-auto px-4 sm:px-6 lg:px-7 xl:px-0">
        <OBreadcrumb class="capitalize" :class="`${isMobile ? 'mt-4 mb-3 ml-3' : 'my-6 ml-4'}`" :items="breadcrumbRoutes" />
        <div class="border-b-2 border-[#FF7352] my-4 sm:my-6">
          <div class="flex">
            <OLink class="flex grow-0 ml-3" :href="getCategoryPath">
              <div class="bg-[#FF7352] rounded-t-md p-3">
                <OIcon
                  :icon="getIconPath(currentCategory.onediocategoryid ? currentCategory.name : 'hand')"
                  svg-class="w-6 h-6 text-white"
                />
              </div>
              <p class="text-[#FF7352] uppercase font-bold text-lg leading-5 my-auto pl-4">{{ currentCategory.name }}</p>
            </OLink>
          </div>
        </div>
        <div class="flex justify-center sm:justify-between flex-col sm:flex-row">
          <div class="w-full mr-0 space-y-4 sm:space-y-6">
            <VitrinArticleCard
              v-for="(article, index) in articles"
              :key="article._id"
              :article="article"
              :data-layer="{ name: `Vitrin - ${currentCategory.name}`, position: index + 1 }"
            />
          </div>

          <div v-if="isMobile && promotions.length">
            <div class="border-b-2 border-[#FF7352] my-4 sm:my-6">
              <div class="flex grow-0 ml-3">
                <div class="bg-[#FF7352] rounded-t-md p-3">
                  <OIcon icon="vitrin/vitrin-promotion-header-logo" svg-class="w-6 h-6 text-white" />
                </div>
                <p class="text-[#FF7352] font-bold text-lg leading-5 my-auto pl-4">VİTRİN ÜRÜNLERİ</p>
              </div>
            </div>
            <div class="space-y-4 sm:space-y-6">
              <VitrinPromotionCard v-for="article in promotions" :key="article._id" :item="article" />
            </div>
          </div>
        </div>
        <OPaginationBase class="text-center mt-7.5" :current-page="feedPagination.page" :has-next-page="feedPagination.hasNextPage" />
      </div>
      <AdController unit="VitrinProject" />
    </template>
  </FullPageLayout>
</template>
<script>
import { defineComponent, useFetch, useContext, ref, useRoute, ssrRef, useMeta } from '@nuxtjs/composition-api';
import FullPageLayout from '@/modules/common/components/layout/FullPageLayout';
import ErrorPageLayout from '@/modules/common/components/layout/ErrorPageLayout.vue';
import { OLoading, OBreadcrumb, OLink, OIcon } from '@/modules/common/components/ui';
import { initializeAdLoaders } from '@/modules/ads/adUtils';
import AdController from '@/modules/ads/components/AdController';
import VitrinHeader from '~/modules/vitrin/components/VitrinHeader';
import { useDevice } from '~/composables/useDevice';
import { normalizeText } from '~/modules/common/utils/stringUtils';
import VitrinArticleCard from '~/modules/vitrin/components/VitrinArticleCard';
import VitrinPromotionCard from '~/modules/vitrin/components/VitrinPromotionCard';
import OPaginationBase from '~/modules/common/components/ui/OPagination/OPaginationBase';
import { useVitrinMeta } from '~/modules/vitrin/utils/vitrinMeta';
import { mergeHeadItems } from '~/modules/common/utils/helpers';
import { IS_ONEDIO } from '~/constants/config';

export default defineComponent({
  components: {
    FullPageLayout,
    ErrorPageLayout,
    OLoading,
    VitrinHeader,
    VitrinArticleCard,
    VitrinPromotionCard,
    OBreadcrumb,
    AdController,
    OPaginationBase,
    OLink,
    OIcon,
  },
  head: {},
  setup() {
    const route = useRoute();
    const { isMobile } = useDevice();
    const { $error, $service, $cache } = useContext();
    const { generateAdConfig } = initializeAdLoaders();
    useMeta(() => {
      const { title, meta, link } = useVitrinMeta(route.value.params.slug, route.value.fullPath);
      return mergeHeadItems({ title }, { meta }, { link });
    });
    $cache('public, max-age=30');
    const routeParams = route.value.params.slug;
    const breadcrumbRoutes = [
      { href: '/vitrin', name: 'vitrin' },
      { href: route.value.fullPath, name: routeParams },
    ];
    const allCategories = ref([]);
    const currentCategory = ssrRef({});
    const getCategoryPath = ref('');
    const articles = ref([]);
    const promotions = ref([]);
    const getIconPath = (name) => `vitrin/icons/${normalizeText(name)}`;
    // for pagination
    const pageNumber = parseInt(route.value.params.pageNum) || 1;
    const feedPagination = ref({});

    const serversideAdConfig = generateAdConfig('onlyGPT');
    const { fetch, fetchState } = useFetch(async () => {
      try {
        if (!IS_ONEDIO) {
          $error('Page not found', 404, false);
        }
        const { data } = await $service.vitrin.getVitrinFeed();
        const { categories } = data;
        allCategories.value = categories;
        currentCategory.value = categories.find((item) => routeParams === normalizeText(item.name));
        if (!currentCategory.value) {
          $error('page not found', 404, false);
        }
        getCategoryPath.value = `/vitrin/${normalizeText(currentCategory.value.name)}`;
        serversideAdConfig(
          ref({
            type: 'Vitrin',
            targetingKey: normalizeText(currentCategory.value.name),
          })
        );
        await getArticles();
      } catch (error) {
        $error(error);
      }
    });

    async function getArticles() {
      try {
        const { data } = await $service.vitrin.getVitrinFeed(currentCategory.value.id, pageNumber);
        const { feed } = data;
        feedPagination.value = feed.pagination;
        articles.value = feed.articles;
        promotions.value = feed.promotions;
      } catch (error) {
        $error(error);
      }
    }

    return {
      isMobile,
      fetch,
      fetchState,
      allCategories,
      currentCategory,
      getCategoryPath,
      articles,
      promotions,
      breadcrumbRoutes,
      getIconPath,
      pageNumber,
      feedPagination,
    };
  },
});
</script>
